'use client';

import { type Post, VoteType } from '@shared/all/domains/posts';
import { formatNumbers } from '@shared/utils/helpers/formatNumbers';
import toast from '@shared/utils/helpers/toast';
import usePostsShared from '@shared/utils/hooks/usePosts';
import { VoteIcon } from '@shared/utils/icons';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { useConfirmIfPrivate } from '../../hooks/useConfirmIfPrivate';
import { client } from '../../utils/trpc';
import { useAuthModals } from '../../widgets/WidgetsContext';

type Props = {
  voteCount: number;
  postId: string;
  userVoted: Post['voted'] | null;
  uniqueId?: string;
  disabled?: boolean;
  minified?: boolean;
};

const Vote = (props: Props) => {
  const { voteHandler } = usePostsShared(client, props.uniqueId);
  const { openAuthContinueModal } = useAuthModals();
  const [isVoting, setIsVoting] = useState(false);
  const { ensureConfirmed, dialog } = useConfirmIfPrivate();

  const iconSize = props.minified ? 14 : 18;

  const handleVote = async (
    e: React.MouseEvent<HTMLButtonElement>,
    type: VoteType
  ) => {
    e.stopPropagation();
    if (isVoting) return;
    if (!(await ensureConfirmed('upvote'))) return;
    setIsVoting(true);
    try {
      await voteHandler(type, props.postId, false, openAuthContinueModal);
    } catch {
      toast('Failed to vote. Please try again.', '', 'error');
    } finally {
      setIsVoting(false);
    }
  };

  return (
    <>
      {dialog}
      <div
        className={`flex items-center justify-center ${props.minified ? 'w-auto' : 'w-[70px]'}`}
      >
        <button
          disabled={props.disabled || isVoting}
          title="Upvote"
          className={`rounded p-0.5 transition-all duration-200 hover:scale-[1.05] hover:bg-background-light active:-translate-y-1 active:scale-[0.95] ${isVoting ? 'cursor-not-allowed opacity-50' : ''}`}
          onClick={(e) => handleVote(e, VoteType.UPVOTE)}
        >
          <VoteIcon
            type={VoteType.UPVOTE}
            size={iconSize}
            filled={props.userVoted === VoteType.UPVOTE}
            color={
              props.userVoted === VoteType.UPVOTE
                ? 'rgb(var(--primary))'
                : 'rgb(var(--text-secondary-color))'
            }
          />
        </button>
        {isVoting ? (
          <Loader2
            size={props.minified ? 10 : 12}
            className="mx-0.5 animate-spin text-textSecondary"
          />
        ) : (
          // `key` is the count, so each new tally is a fresh element and the
          // CSS enter animation replays. The old `exit` variant never ran —
          // there was no AnimatePresence above this.
          <span
            key={props.voteCount}
            className={`anim-enter mx-0.5 min-w-4 text-center text-textSecondary [--enter-duration:200ms] [--enter-scale:0.5] ${props.minified ? 'text-[10px]' : 'text-xs'}`}
          >
            {formatNumbers(props.voteCount)}
          </span>
        )}
        <button
          disabled={props.disabled || isVoting}
          title="Downvote"
          className={`rounded p-0.5 transition-all duration-200 hover:scale-[1.05] hover:bg-background-light active:translate-y-1 active:scale-[0.95] ${isVoting ? 'cursor-not-allowed opacity-50' : ''}`}
          onClick={(e) => handleVote(e, VoteType.DOWNVOTE)}
        >
          <VoteIcon
            type={VoteType.DOWNVOTE}
            size={iconSize}
            filled={props.userVoted === VoteType.DOWNVOTE}
            color={
              props.userVoted === VoteType.DOWNVOTE
                ? 'rgb(var(--primary))'
                : 'rgb(var(--text-secondary-color))'
            }
          />
        </button>
      </div>
    </>
  );
};

export default Vote;
